/**
 * Lightweight compliance status polling — backoff + pause when tab hidden.
 * Full GET /compliance runs only when status reports an update or job completion.
 */

export const COMPLIANCE_STATUS_POLL_MAX_ATTEMPTS = 720;

export function getCompliancePollDelayMs(elapsedMs) {
  if (elapsedMs < 2 * 60 * 1000) return 5000;
  if (elapsedMs < 10 * 60 * 1000) return 15000;
  if (elapsedMs < 60 * 60 * 1000) return 30000;
  return 60000;
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function pollComplianceStatus({
  getStatus,
  fetchActivity,
  onRefresh,
  maxAttempts = COMPLIANCE_STATUS_POLL_MAX_ATTEMPTS,
  shouldAbort = () => false,
}) {
  const startedAt = Date.now();
  let attempts = 0;
  let baselineUpdatedAt = null;
  // Do not treat "not processing yet" as done — tracking often lags the API accept
  // by a few seconds, which previously fired a false "completed" toast.
  let sawProcessing = false;

  while (attempts < maxAttempts) {
    if (shouldAbort()) {
      return { done: false, reason: 'aborted' };
    }
    if (typeof document !== 'undefined' && document.hidden) {
      await sleep(getCompliancePollDelayMs(Date.now() - startedAt));
      continue;
    }

    attempts += 1;
    try {
      const { data } = await getStatus();
      const status = data?.data;
      if (fetchActivity) {
        await fetchActivity(1);
      }

      const activeJobs = status?.active_jobs;
      const hasActiveJobs = Array.isArray(activeJobs) && activeJobs.length > 0;
      const isProcessing = Boolean(status?.processing) || hasActiveJobs;
      if (isProcessing) sawProcessing = true;

      const updatedAt = status?.compliance_updated_at ?? null;
      if (baselineUpdatedAt === null) {
        baselineUpdatedAt = updatedAt;
      } else if (updatedAt && updatedAt !== baselineUpdatedAt && sawProcessing && !isProcessing) {
        const full = onRefresh ? await onRefresh() : null;
        return { done: true, reason: 'updated', status, full };
      }

      if (sawProcessing && !isProcessing) {
        const full = onRefresh ? await onRefresh() : null;
        return { done: true, reason: 'completed', status, full };
      }
    } catch (_) {
      // keep polling until max attempts
    }

    await sleep(getCompliancePollDelayMs(Date.now() - startedAt));
  }

  return { done: false, reason: 'timeout' };
}
